import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { FileObject, LoginFormData, UserData } from 'appRootPath/shared/global-meta-data';

@Injectable({
  providedIn: 'root'
})
export class CpMarketplaceApiService {
  private apiPath = environment.serverUrl;

  private endpoints = {
    userLogin: '/users/loginnew/',
    login_multi_company_user: '/users/login_multi_company_user/',
    logout: '/users/logout/',
    forgotPasswordRequest: '/users/forgot_password/',
    setPassword: '/users/set_password_confirm/',
    corporateSignUp: '/users/corporate_user_view/',
    partyUsers: '/users/user_profile/',
    partyUserInfoAfterLogin: '/users/user_profile_login/',
    userView: '/users/user_view/',
    createDivision: '/divisionrouter/',
    createCP: '/users/cpcreate/',
    createIssue: '/users/issuecreate/',
    corporateUserInfo: '/users/corporate_user_view_login/',
    corporateUserViewRoleBased: '/users/corporate_user_view_role_based/',
    divisions: '/users/division/',
    accounts: 'accounts/',
    instruments: '/users/getinstrument/',
    role: '/getrole/',
    bids: '/auction/bid/listbids/',
    createBid: '/auction/bid/createupdatebid/',
    createClosedBid: '/auction/bid/create_closed_bid_by_issuer/',
    issueInvestors: '/users/issueinvestordetails/',
    // investor: '/users/corporateuserprofile/get_all_investor_corporate/',
    generateDocs: '/documents/generate_pdf/',
    generateCsv: '/documents/generate_csv/',
    generateMCF: '/master_creation_form/generate_mcf_pdf/',
    generateLOO: '/documents/generate_loo_pdf/',
    generateDCN: '/documents/generate_dcn_pdf/',
    notifyMCF: '/master_creation_form/notify_mcf/',
    boardResolution: '/users/boardresolution/',
    signatories: '/users/signatorydoc/',
    perDocSignatories: '/users/perdocsignatories/',
    cpSignatories: '/users/cpsignatories/',
    userCreditRatingUpdate: '/users/creditrating/',
    CPDocsInfo: '/users/CPViewSetDocumentation/',
    issueDocsInfo: '/users/IssueViewSetDocumentation/',
    perDocAdditionalData: '/users/perdocextradata/',
    investorUsers: '/users/get_investor_company_users/',
    moveState: '/state_machines/call_submit_move_state/',
    uploadSignedDocuments: '/documents/upload_docs/',
    ipaCheckList: '/documents/ipachecklist/',
    holidays: '/users/holidays/',
    uploadFile: '/documents/upload_file/',
    offPlatformInvestor: '/users/corporate_user_view_login/create_dummy_investor_company/',
    file_upload: '/core/upload_file_get_key/',
    get_otp: '/users/get_temp_otp_token/',
    resend_otp: '/users/resend_otp/',
    market_listings: '/documents/listing_details/',
    market_listing_extra_data: '/documents/add_extra_data_listing/',
    get_fileurl_from_key: '/core/get_file_obj_from_keys/',
    get_credential_public_key: '/users/get_credential_public_key/'
  };

  /**
   * constructor for the service class
   * @param http inject http client module
   */
  constructor(public http: HttpClient) { }

  /**
   * generic error handler
   * @param error captured error
   * @returns observable error
   */
  errorHandler(error) {
    if (error) {
      return throwError(error);
    }
  }

  /**
   * gets query param string from object
   * @param queryParams plain object with keys and values (values not arrays or nested objects)
   * @returns string of type abc=123&pqr=456
   */
  getQueryStr(queryParams) {
    let params = new HttpParams();
    Object.keys(queryParams).forEach((key) => {
      params = params.set(key, queryParams[key]);
    });

    const paramStr = params.toString();
    return paramStr ? '?' + paramStr : '';
  }

  fetchFile(fileUrl) {
    return this.http.get(this.apiPath + fileUrl).pipe(
      catchError(this.errorHandler)
    );
  }

  /**
   * From the file key database field, get the s3 file details object
   * @param fileKey the file key
   */
  getFileObj(fileKey): Observable<FileObject> {
    const postData = {
      file_keys: [fileKey]
    };
    return this.http.post<FileObject>(
      this.apiPath + this.endpoints.get_fileurl_from_key, postData
    ).pipe(
      catchError(this.errorHandler)
    );
  }

  downloadIpaCsv(fileParams) {
    return this.http.post(this.apiPath + this.endpoints.generateCsv, fileParams).pipe(
      catchError(this.errorHandler)
    );
  }

  getOtp(getOtpData) {
    return this.http
      .post(this.apiPath + this.endpoints.get_otp, getOtpData).pipe(
        catchError(this.errorHandler)
      );
  }

  resendOtp(resendOtpData) {
    return this.http
      .post(this.apiPath + this.endpoints.resend_otp, resendOtpData).pipe(
        catchError(this.errorHandler)
      );
  }

  logout() {
    return this.http
      .post(this.apiPath + this.endpoints.logout, {}).pipe(
        catchError(this.errorHandler)
      );
  }
  /**
   * Makes the API call to handle to user login. Encodes the loginDetails before making an API call.
   *
   * @param loginData - Login Form Data.
   */
  login(loginData: LoginFormData): Observable<UserData> {
    return this.http
      .post<UserData>(this.apiPath + this.endpoints.userLogin, loginData).pipe(
        catchError(this.errorHandler)
      );
  }

  loginWithCompany(companyId) {
    return this.http.post(this.apiPath + this.endpoints.login_multi_company_user,
      { company_id: companyId }).pipe(
        catchError(this.errorHandler)
      );
  }

  uploadFileAndGetToken(formData) {
    return this.http.post(this.apiPath + this.endpoints.file_upload, formData).pipe(
      catchError(this.errorHandler)
    );
  }

  setPassword(apiParams) {
    return this.http.post(this.apiPath + this.endpoints.setPassword, apiParams).pipe(
      catchError(this.errorHandler)
    );
  }

  forgotPassword(username) {
    return this.http.post(this.apiPath + this.endpoints.forgotPasswordRequest, { username })
      .pipe(
        catchError(this.errorHandler)
      );
  }

  resetPassword(apiParams) {
    return this.http.post(this.apiPath + this.endpoints.setPassword, apiParams)
      .pipe(
        catchError(this.errorHandler)
      );
  }

  // !ToDo File Upload
  corporateSignUp(signUpDetails) {
    return this.http
      .post(this.apiPath + this.endpoints.corporateSignUp, signUpDetails)
      .pipe(
        catchError(this.errorHandler)
      );
  }

  createPartyUsers(partyUserDetails) {
    return this.http
      .post(this.apiPath + this.endpoints.partyUsers, partyUserDetails)
      .pipe(
        catchError(this.errorHandler)
      );
  }

  updateUserTnc(userId, tnc) {
    return this.http.patch(this.apiPath + this.endpoints.userView + `${userId}/`, { tnc_signed: tnc })
      .pipe(
        catchError(this.errorHandler)
      );
  }

  getPublicKey() {
    return this.http
      .get(this.apiPath + this.endpoints.get_credential_public_key)
      .pipe(
        catchError(this.errorHandler)
      );
  }

  getAllHolidays() {
    return this.http
      .get(this.apiPath + this.endpoints.holidays)
      .pipe(
        catchError(this.errorHandler)
      );
  }

  getIpaCheckList(issueId) {
    return this.http
      .get(this.apiPath + this.endpoints.ipaCheckList + `?issue=${issueId}/`)
      .pipe(
        catchError(this.errorHandler)
      );
  }

  saveIpaChecklist(ipaChecklist) {
    return this.http
      .post(this.apiPath + this.endpoints.ipaCheckList, ipaChecklist)
      .pipe(
        catchError(this.errorHandler)
      );
  }

  uploadLetterhead(uploadData, corporateId) {
    return this.http
      .patch(this.apiPath + this.endpoints.corporateUserInfo + corporateId + '/', uploadData)
      .pipe(
        catchError(this.errorHandler)
      );
  }

  /**
   * For a given division id, the list of all CPs that are created for the division are returned.
   * @param divisionId - Id of the division.
   * @returns - List of all CPs for the division.
   */
  getListOfCP(divisionId) {
    return this.http
      .get(this.apiPath + this.endpoints.createCP + `?division=${divisionId}`)
      .pipe(
        catchError(this.errorHandler)
      );
  }

  getListOfIssues(cpId) {
    return this.http
      .get(this.apiPath + this.endpoints.createIssue + `?cp__id=${cpId}`)
      .pipe(
        catchError(this.errorHandler)
      );
  }

  /**
   * For the given cp details, performs a post request to the server and returns the response.
   * @param cpDetails - Object containing all the details of a cp to create.
   * @returns - Created CP details.
   */
  createCP(cpDetails) {
    return this.http
      .post(this.apiPath + this.endpoints.createCP, cpDetails)
      .pipe(
        catchError(this.errorHandler)
      );
  }

  /**
   * For the given cp details, performs a post request to the server and returns the response.
   * @param cpDetails - Object containing all the details of a cp to create.
   * @returns - Created CP details.
   */
  updateCP(cpId, cpDetails) {
    return this.http
      .patch(this.apiPath + this.endpoints.createCP + `${cpId}/`, cpDetails)
      .pipe(
        catchError(this.errorHandler)
      );
  }
  /**
   * For a CP ID, returns the complete information about the CP.
   * @param cpId - ID of the CP.
   * @returns - Complete details of a CP.
   */
  getCPInfo(cpId) {
    return this.http
      .get(this.apiPath + this.endpoints.createCP + `${cpId}/`).pipe(
        catchError(this.errorHandler)
      );
  }

  /**
   * For a CP ID, returns the complete information about the CP.
   * @param cpId - ID of the CP.
   * @returns - Complete details of a CP.
   */
  getCPDocInfo(cpId = null) {
    const cpInfoApiCall = cpId
      ? this.http.get(this.apiPath + this.endpoints.CPDocsInfo + `?id=${cpId}`)
      : this.http.get(this.apiPath + this.endpoints.CPDocsInfo);

    return cpInfoApiCall.pipe(
      catchError(this.errorHandler)
    );
  }

  getIssueDocInfo(issueId = null, queryParams = {}) {
    if (issueId) {
      queryParams['id'] = issueId;
    }
    const paramStr = this.getQueryStr(queryParams);

    const issueInfoApiCall = this.http.get(`${this.apiPath}${this.endpoints.issueDocsInfo}${paramStr}`);

    return issueInfoApiCall.pipe(
      catchError(this.errorHandler)
    );
  }

  getIssueInfo(issueId) {
    return this.http
      .get(this.apiPath + this.endpoints.createIssue + `${issueId}/`)
      .pipe(
        catchError(this.errorHandler)
      );
  }

  getIssueDetails(issueId: string) {
    return this.http
      .get(this.apiPath + this.endpoints.createIssue + `${issueId}/get_issue_details/`)
      .pipe(
        catchError(this.errorHandler)
      );
  }

  /**
   * For a division id, gets the division information.
   * @param divisionId - ID of the division.
   */
  getDivisionInfo(divisionId) {
    return this.http
      .get(this.apiPath + this.endpoints.createDivision + `${divisionId}/`)
      .pipe(
        catchError(this.errorHandler)
      );
  }

  /**
   * For the given issue details, performs a post request to the server and returns the response.
   * @param issueDetails - Object containing all the details of an issue to create.
   * @returns - Created issue details.
   */
  createIssue(issueDetails) {
    return this.http
      .post(this.apiPath + this.endpoints.createIssue, issueDetails)
      .pipe(
        catchError(this.errorHandler)
      );
  }

  patchIssue(issueDetails) {
    return this.http
      .patch(`${this.apiPath}${this.endpoints.createIssue}${issueDetails.id}/`, issueDetails)
      .pipe(
        catchError(this.errorHandler)
      );
  }

  deleteIssue(issueId) {
    return this.http
      .delete(`${this.apiPath}${this.endpoints.createIssue}${issueId}/`)
      .pipe(
        catchError(this.errorHandler)
      );
  }

  createBid(bidDetails) {
    return this.http
      .post(this.apiPath + this.endpoints.createBid, bidDetails)
      .pipe(
        catchError(this.errorHandler)
      );
  }

  getAllUserInfo() {
    return this.http.get(this.apiPath + this.endpoints.partyUserInfoAfterLogin).pipe(
      catchError(this.errorHandler)
    );
  }

  getCurrentUserInfo(userId) {
    return this.http
      .get(this.apiPath + this.endpoints.partyUserInfoAfterLogin + `${userId}/`)
      .pipe(
        catchError(this.errorHandler)
      );
  }

  getDivision() {
    return this.http.get(this.apiPath + this.endpoints.divisions).pipe(
      catchError(this.errorHandler)
    );
  }

  getInstruments(divisionId) {
    return this.http
      .get(
        this.apiPath + this.endpoints.instruments + `division=${divisionId}/`
      )
      .pipe(
        catchError(this.errorHandler)
      );
  }

  getRole(divisionId, instrumentId) {
    return this.http
      .get(
        this.apiPath +
        this.endpoints.role +
        `division=${divisionId}&instrument=${instrumentId}/`
      )
      .pipe(
        catchError(this.errorHandler)
      );
  }

  getBids(bidParams) {
    return this.http
      .post(this.apiPath + this.endpoints.bids, bidParams)
      .pipe(
        catchError(this.errorHandler)
      );
  }

  /**
   * Gets and returns all the issues.
   * @returns - All the issues available in the system.
   */
  getAllIssues() {
    return this.http.get(this.apiPath + this.endpoints.createIssue).pipe(
      catchError(this.errorHandler)
    );
  }

  updateIssue(issueDetails) {
    return this.http
      .patch(
        this.apiPath + this.endpoints.createIssue + `${issueDetails.id}/`,
        issueDetails
      )
      .pipe(
        catchError(this.errorHandler)
      );
  }

  updateInvestorForIssue(investorIssueData) {
    return this.http
      .post(this.apiPath + this.endpoints.issueInvestors, investorIssueData)
      .pipe(
        catchError(this.errorHandler)
      );
  }

  generateDocs(docParams) {
    return this.http
      .post(this.apiPath + this.endpoints.generateDocs, docParams)
      .pipe(
        catchError(this.errorHandler)
      );
  }

  /**
   * Gets the board resolution for the user.
   */
  getBoardResolution(valueDate = null) {
    let apiPath = this.apiPath + this.endpoints.boardResolution;

    if (valueDate) {
      apiPath = apiPath + `?value_date=${valueDate}`;
    }

    return this.http.get(apiPath).pipe(
      catchError(this.errorHandler)
    );
  }

  createBoardResolution(brDetails) {
    return this.http
      .post(this.apiPath + this.endpoints.boardResolution, brDetails)
      .pipe(
        catchError(this.errorHandler)
      );
  }

  patchBoardResolution(brId, brDetails) {
    return this.http
      .patch(
        this.apiPath + this.endpoints.boardResolution + `${brId}/`,
        brDetails
      )
      .pipe(
        catchError(this.errorHandler)
      );
  }

  /**
   * Resends email to the signatories who have not signed the documents.
   * @param issueId - Issue ID.
   */
  resendEmailToSignatories(issueId: string) {
    return this.http
      .post(this.apiPath + this.endpoints.issueDocsInfo + `${issueId}/resend_signing_email/`, {});
  }

  getSignatories(cpId = null) {
    const signatoriesListAPI = cpId
      ? this.http.get(this.apiPath + this.endpoints.cpSignatories + `?cp__id=${cpId}`)
      : this.http.get(this.apiPath + this.endpoints.signatories);

    return signatoriesListAPI.pipe(
      catchError(this.errorHandler)
    );
  }

  createSignatories(signatoryObj) {
    return this.http
      .post(this.apiPath + this.endpoints.signatories, signatoryObj)
      .pipe(
        catchError(this.errorHandler)
      );
  }

  getPerDocSignatories(issueId) {
    return this.http
      .get(
        this.apiPath + this.endpoints.perDocSignatories + `?issue__id=${issueId}`
      )
      .pipe(
        catchError(this.errorHandler)
      );
  }

  setSelectedSignatories(signatoriesData) {
    return this.http.post(
      this.apiPath + this.endpoints.perDocSignatories,
      signatoriesData
    )
      .pipe(
        catchError(this.errorHandler)
      );
  }

  /**
   * Posts the users-creditrating-update
   * @param creditRatingDetails - Credit rating details to be posted to backend.
   */
  creditRatingUpdate(creditRatingDetails) {
    return this.http.post(this.apiPath + this.endpoints.userCreditRatingUpdate, creditRatingDetails)
      .pipe(
        catchError(this.errorHandler)
      );
  }

  getCreditRatings() {
    return this.http.get(this.apiPath + this.endpoints.userCreditRatingUpdate)
      .pipe(
        catchError(this.errorHandler)
      );
  }

  postAdditionalData(additionalData) {
    return this.http.post(this.apiPath + this.endpoints.perDocAdditionalData, additionalData)
      .pipe(
        catchError(this.errorHandler)
      );
  }

  moveState(issueId, moveStateData) {
    return this.http.post(
      this.apiPath + this.endpoints.moveState + `issue_id=${issueId}/`,
      moveStateData).pipe(
        map((res: Response, statusCodeNumber) => ({ apiResponse: res, status: statusCodeNumber === 0 ? 'success' : 'error' })),
        catchError(this.errorHandler)
      );
  }

  postSignedDocuments(signedDocsDetails) {
    return this.http.post(this.apiPath + this.endpoints.uploadSignedDocuments, signedDocsDetails)
      .pipe(
        catchError(this.errorHandler)
      );
  }

  uploadFile(fileDetails) {
    return this.http.post(this.apiPath + this.endpoints.uploadFile, fileDetails)
      .pipe(
        catchError(this.errorHandler)
      );
  }

  getAllInvestors(term): Observable<any> {
    // Role Codes are '1' - Issuer, '2' - IPA and '3' - Investor.
    return this.http.get(
      this.apiPath + this.endpoints.corporateUserViewRoleBased + '?role=3'
    ).pipe(
      catchError(this.errorHandler),
      map(response => this.searchTermInCompanyNames(response, term))
    );
  }

  getAllInvestorsWithUsers(): Observable<any> {
    return this.http.get(this.apiPath + this.endpoints.investorUsers).pipe(
      catchError(this.errorHandler)
    );
  }

  /**
   * Returns investors with company name matching the term
   * @param term the term to search
   */
  getAllInvestorsWithSearch(term): Observable<any> {
    return this.http.get(this.apiPath + this.endpoints.investorUsers).pipe(
      map(response => Object.values(response)),
      map((investors: any) => investors.map(
        (investor: any) => ({
          company_name: investor.company_name,
          id: investor.user_ids[0]
        })
      )),
      map(response => this.searchTermInCompanyNames(response, term))
    );
  }

  /**
   * Find investor company names containing term
   * @param response investor company name and id object
   * @param term the term to match in the company name
   */
  searchTermInCompanyNames(investorData, term) {
    const retVal = investorData.filter((resp) => {
      if (!term) {
        return true;
      } else {
        return resp.company_name.toLowerCase().includes(term.toLowerCase());
      }
    });
    return retVal ? retVal : investorData;
  }

  createClosedBid(bidDetails) {
    return this.http
      .post(this.apiPath + this.endpoints.createClosedBid, bidDetails)
      .pipe(
        catchError(this.errorHandler)
      );
  }

  createOfflineInvestor(investorDetails) {
    return this.http
      .post(this.apiPath + this.endpoints.offPlatformInvestor, investorDetails)
      .pipe(
        catchError(this.errorHandler)
      );
  }
  getExistingListingDocs(listingDate) {
    return this.http.
      get(this.apiPath + this.endpoints.market_listings + `?listing_date=${listingDate.listing_date}&stock_exchange=${listingDate.stock_exchange}`).
      pipe(
        catchError(this.errorHandler)
      );
  }

  getFreshListingDocs(listingData) {
    return this.http.post(this.apiPath + this.endpoints.market_listings, listingData).
      pipe(catchError(this.errorHandler));
  }

  generateListingDoc(documentId, valueDate) {
    return this.http.get(
      `${this.apiPath}/documents/${documentId}/generate/?value_date=${valueDate}`
    ).pipe(
      catchError(this.errorHandler)
    );
  }

  sendListingForSign(listingId, listingData) {
    return this.http.post(
      `${this.apiPath}${this.endpoints.market_listings}${listingId}/send_docs_for_signing/`,
      listingData).pipe(
        catchError(this.errorHandler)
      );
  }

  saveListingExtraData(listingId, docId, postData) {
    return this.http.post(
      `${this.apiPath}${this.endpoints.market_listing_extra_data}?listing_id=${listingId}&doc_name=${docId}`,
      postData).pipe(
        catchError(this.errorHandler)
      );
  }

  getFtrackReport(issueId) {
    const path = `${this.apiPath}${this.endpoints.issueDocsInfo}${issueId}/get_ftrac_report/`;
    return this.http.get(path).pipe(
      catchError(this.errorHandler)
    );
  }
}
